import { useQuery } from "@tanstack/react-query";
import { useGetList } from "ra-core";
import { useEffect, useMemo, useState } from "react";

import { fetchExperimentSummary } from "../experiments/experimentClient";
import type { FunnelSummary } from "../funnel/funnelSummary";
import { DEFAULT_MONETIZATION_EXPERIMENT } from "../monetization/monetizationExperiments";
import { headlineMetrics } from "../monetization/monetizationModel";
import { monetizationQueryKeys } from "../monetization/monetizationQueryKeys";
import {
  dashboardRequest,
  isDashboardDataProviderEnabled,
} from "../providers/aws/client";
import type {
  Contact,
  EchoflicksVideoSession,
  MarketingMetric,
  WhatsAppBroadcast,
} from "../types";
import {
  computeWeekWindows,
  dailyDeliveries,
  splitStudioFilms,
  summarizeBroadcasts,
  summarizeEchoes,
  summarizeMarketing,
  summarizeStudioFilms,
  topOccasions,
  type WeekWindows,
} from "./executiveMetrics";

// Data hooks for the executive dashboard. Each hook owns one request and
// degrades on its own: a failing marketing rollup never blanks the echo tiles.

/** How long a tile may show cached numbers before refetching in the background. */
const LIVE_STALE_MS = 60 * 1000;
/** The marketing rollup and funnel aggregate only change daily / per event. */
const AGGREGATE_STALE_MS = 5 * 60 * 1000;
const WINDOW_TICK_MS = 5 * 60 * 1000;

const EMPTY: never[] = [];

/**
 * Week boundaries that re-evaluate every five minutes. `now` is rounded to the
 * same tick inside computeWeekWindows, so query keys built from these values
 * stay stable between ticks instead of refetching on every render.
 */
export function useWeekWindows(): WeekWindows {
  const [windows, setWindows] = useState(() => computeWeekWindows());
  useEffect(() => {
    const id = window.setInterval(() => {
      setWindows((previous) => {
        const next = computeWeekWindows();
        return next.now === previous.now ? previous : next;
      });
    }, WINDOW_TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  return windows;
}

/**
 * Echoes touched since last Monday — enough for this week, the like-for-like
 * baseline and the 14-day trend in one request. The `display_date@gte` filter
 * is pushed to the dashboard API (see providers/aws/echoesPath.ts) so only the
 * recent slice of the partition is downloaded.
 *
 * B4.4: Studio (agency portal) films are split out first. Every B2C number
 * (delivered, failed, ratings, the daily trend, occasions) counts WhatsApp
 * Echoes only; the Studio films of the week are their own number.
 */
export function useWeeklyEchoes(windows: WeekWindows) {
  const query = useGetList<EchoflicksVideoSession>(
    "echoflicks_echoes",
    {
      pagination: { page: 1, perPage: 10_000 },
      sort: { field: "display_date", order: "DESC" },
      filter: { "display_date@gte": windows.lastWeekStart },
    },
    { staleTime: LIVE_STALE_MS, retry: 1 },
  );
  const echoes = query.data ?? EMPTY;
  const { b2c, studio } = useMemo(() => splitStudioFilms(echoes), [echoes]);
  const comparison = useMemo(
    () => summarizeEchoes(b2c, windows),
    [b2c, windows],
  );
  const daily = useMemo(() => dailyDeliveries(b2c, windows), [b2c, windows]);
  const occasions = useMemo(() => topOccasions(b2c, windows), [b2c, windows]);
  const studioFilms = useMemo(
    () => summarizeStudioFilms(studio, windows),
    [studio, windows],
  );
  return {
    isPending: query.isPending,
    error: query.error,
    dataUpdatedAt: query.dataUpdatedAt,
    comparison,
    daily,
    occasions,
    studioFilms,
  };
}

function useContactCount(filter: Record<string, string>) {
  const query = useGetList<Contact>(
    "contacts",
    { pagination: { page: 1, perPage: 1 }, filter },
    { staleTime: LIVE_STALE_MS, retry: 1 },
  );
  return {
    total: query.total ?? null,
    isPending: query.isPending,
    error: query.error,
  };
}

/**
 * New contacts (first seen) this week, with the same point last week.
 * B4.4: Studio films' phantom `studio-…` contacts never carry `first_seen`
 * (the dashboard indexer, which creates them, never writes that field; only
 * imported WhatsApp contacts have it), so the server-side `first_seen` window
 * already leaves them out.
 */
export function useNewContacts(windows: WeekWindows) {
  const current = useContactCount({ "first_seen@gte": windows.thisWeekStart });
  const previous = useContactCount({
    "first_seen@gte": windows.lastWeekStart,
    "first_seen@lt": windows.lastWeekSamePoint,
  });
  return {
    current: current.total,
    previous: previous.total,
    isPending: current.isPending,
    error: current.error ?? previous.error,
  };
}

/** Marketing rollup rows for last week and this week (admin only). */
export function useWeeklyMarketing(windows: WeekWindows) {
  const query = useGetList<MarketingMetric>(
    "marketing_metrics",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "period", order: "DESC" },
      filter: { "period@gte": windows.lastWeekStart.slice(0, 10) },
    },
    { staleTime: AGGREGATE_STALE_MS, retry: 1 },
  );
  const rows = query.data ?? EMPTY;
  const comparison = useMemo(
    () => summarizeMarketing(rows, windows),
    [rows, windows],
  );
  return {
    isPending: query.isPending,
    error: query.error,
    hasRows: rows.length > 0,
    comparison,
  };
}

/** This ISO week's onboarding cohort from the dashboard API (admin only). */
export function useThisWeekFunnel() {
  const enabled = isDashboardDataProviderEnabled();
  const query = useQuery({
    queryKey: ["executive-dashboard", "funnel-summary", "this_week"],
    enabled,
    staleTime: AGGREGATE_STALE_MS,
    retry: 1,
    queryFn: () =>
      dashboardRequest<{ data: FunnelSummary }>(
        "/funnel/summary?range=this_week",
      ).then((response) => response.data),
  });
  return {
    available: enabled,
    isPending: enabled && query.isPending,
    error: query.error,
    summary: query.data ?? null,
  };
}

/**
 * All-time sales of the live contact paywall from the dashboard API (admin
 * only). Shares its cache key with the Monetization tab, so the two agree.
 */
export function useContactPaywallSales() {
  const enabled = isDashboardDataProviderEnabled();
  const experiment = DEFAULT_MONETIZATION_EXPERIMENT;
  const query = useQuery({
    queryKey: monetizationQueryKeys.summary(experiment.id),
    enabled,
    staleTime: AGGREGATE_STALE_MS,
    retry: 1,
    queryFn: () => fetchExperimentSummary(experiment.id),
  });
  return {
    available: enabled,
    isPending: enabled && query.isPending,
    error: query.error,
    metrics: headlineMetrics(query.data, experiment) ?? null,
  };
}

/** WhatsApp broadcasts created this week and their reach so far. */
export function useWeeklyBroadcasts(windows: WeekWindows) {
  const query = useGetList<WhatsAppBroadcast>(
    "whatsapp_broadcasts",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "created_at", order: "DESC" },
    },
    { staleTime: LIVE_STALE_MS, retry: 1 },
  );
  const broadcasts = query.data ?? EMPTY;
  const summary = useMemo(
    () => summarizeBroadcasts(broadcasts, windows),
    [broadcasts, windows],
  );
  return { isPending: query.isPending, error: query.error, summary };
}
